import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Sidebar          from '../components/Sidebar';
import DatasetSelector  from '../components/DatasetSelector';
import ERDCanvas        from '../components/ERDCanvas';
import RawJSON          from '../components/RawJSON';
import TablesView       from '../components/TablesView';
import UsersAdmin       from '../components/UsersAdmin';
import UploadAdmin      from '../components/UploadAdmin';
import SearchView       from '../components/SearchView';

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdmin  = user?.role === 'admin';

  const [tab,          setTab]          = useState('datasets');
  const [selectedMeta, setSelectedMeta] = useState(null);
  const [fullData,     setFullData]     = useState(null);
  const [loadingData,  setLoadingData]  = useState(false);
  const [searchQuery,  setSearchQuery]  = useState('');

  useEffect(() => {
    if (!selectedMeta) { setFullData(null); return; }
    setLoadingData(true);
    axios.get(`/api/datasets/${selectedMeta._id}`)
      .then(r => setFullData(r.data.data))
      .catch(console.error)
      .finally(() => setLoadingData(false));
  }, [selectedMeta]);

  const handleSelect = (ds) => {
    setSelectedMeta(ds);
    setTab('erd');
  };

  const handleSearchChange = (q) => {
    setSearchQuery(q);
    if (q.trim() && fullData) setTab('search');
  };

  const renderContent = () => {
    if (tab === 'search')   return loadingData ? <Loading /> : <SearchView data={fullData} query={searchQuery} />;
    if (tab === 'datasets') return <DatasetSelector selected={selectedMeta} onSelect={handleSelect} />;
    if (tab === 'erd')      return loadingData ? <Loading /> : <ERDCanvas data={fullData} />;
    if (tab === 'json')     return loadingData ? <Loading /> : <RawJSON data={fullData} />;
    if (tab === 'tables')   return loadingData ? <Loading /> : <TablesView data={fullData} />;
    if (tab === 'users'  && isAdmin) return <UsersAdmin />;
    if (tab === 'upload' && isAdmin) return <UploadAdmin />;
    return null;
  };

  const needsScroll = tab !== 'erd';

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar
        active={tab}
        onNav={setTab}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        hasDataset={!!fullData}
      />
      <main style={{
        flex: 1, overflow: needsScroll ? 'auto' : 'hidden',
        padding: tab === 'erd' ? 0 : 28,
        display: 'flex', flexDirection: 'column',
        background: 'var(--bg)',
        position: 'relative',
      }}>
        {/* Top banner with dataset picker for non-admin tabs */}
        {tab !== 'datasets' && tab !== 'users' && tab !== 'upload' && tab !== 'erd' && (
          <div style={{ padding: '0 0 16px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>Dataset:</span>
            <DatasetPicker selected={selectedMeta} onSelect={setSelectedMeta} />
          </div>
        )}

        {tab === 'erd' && (
          <div style={{ position: 'absolute', top: 10, left: 12, zIndex: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, color: '#64748b', letterSpacing: '.04em' }}>DATASET:</span>
            <DatasetPicker selected={selectedMeta} onSelect={setSelectedMeta} forceDark />
          </div>
        )}

        <div style={{ flex: 1, overflow: tab === 'erd' ? 'hidden' : 'visible', minHeight: 0 }}>
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

function Loading() {
  return <div style={{ padding: 40, color: 'var(--muted)', fontSize: 12 }}>Loading dataset…</div>;
}

function DatasetPicker({ selected, onSelect, forceDark }) {
  const [datasets, setDatasets] = useState([]);
  useEffect(() => {
    axios.get('/api/datasets').then(r => setDatasets(r.data)).catch(console.error);
  }, []);

  const darkStyle = {
    background: '#131c2e', border: '1px solid #1e293b', color: '#e2e8f0',
    fontFamily: 'JetBrains Mono, monospace', fontSize: 11, padding: '5px 10px',
    borderRadius: 8, cursor: 'pointer',
  };
  const themedStyle = {
    background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text)',
    fontFamily: 'JetBrains Mono, monospace', fontSize: 11, padding: '5px 10px',
    borderRadius: 8, cursor: 'pointer',
  };

  return (
    <select
      value={selected?._id || ''}
      onChange={e => {
        const ds = datasets.find(d => d._id === e.target.value);
        onSelect(ds || null);
      }}
      style={forceDark ? darkStyle : themedStyle}>
      <option value="">— select dataset —</option>
      {datasets.map(ds => <option key={ds._id} value={ds._id}>{ds.name}</option>)}
    </select>
  );
}
